import {
  Box,
  Divider,
  Flex,
  ModalCloseButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'ui-components';
import clientApi from '~/clientApi';
import Button from '~/components/Button';
import { ConfirmDiscardChangesOnLeaveModal } from '~/components/ConfirmDiscardChangesOnLeaveModal';
import Modal from '~/components/Modal/Modal';
import { StepperForm } from '~/components/StepperForm';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { closeModal } from '~/ducks/ui';
import { MEDIA_PACK } from '~/ducks/ui.constants';
import { MEDIA_PACK_ENTITY_QUERY_KEY } from '~/queries/keys';
import {
  CreatorType,
  DefaultMediaPackType,
  GenericIndexSignature,
} from '~/types';
import { useBreakpointValue } from '~/utils-ts';

import { getStepStatus } from '../../MediaPackDetailDefaultScreen.helpers';
import { STEPS } from './MediaPackDetailEditModal.constants';

interface Props {
  creator: CreatorType;
  isOpen: boolean;
  mediaPack: GenericIndexSignature<DefaultMediaPackType>;
  onClose: () => void;
}

export const MediaPackDetailEditModal: React.FC<Props> = ({
  creator,
  isOpen,
  mediaPack,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const {
    isOpen: isDiscardModalOpen,
    onClose: onDiscardModalClose,
    onOpen: onDiscardModalOpen,
  } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const snackbar = useSnackbar();
  const dispatch = useDispatch();

  let hasChanged = false;

  const handleSubmit = async (params: Partial<DefaultMediaPackType>) => {
    try {
      await clientApi.mediaPacks.update({
        id: mediaPack.id,
        ...params,
        _impersonateOnce: creator.id,
      });
      await queryClient.invalidateQueries({
        queryKey: [MEDIA_PACK_ENTITY_QUERY_KEY, mediaPack.id],
      });
      dispatch(closeModal(MEDIA_PACK.EDIT_MODAL_ID));
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
    }
  };

  const handleCancel = () => {
    if (hasChanged) {
      onDiscardModalOpen();
    } else {
      onClose();
    }
  };

  const handleDiscard = () => {
    onDiscardModalClose();
    onClose();
  };

  const steps = STEPS({ creator, mediaPack });

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        size="2xl"
        isFullScreenOnMobile
        closeOnEsc
        closeOnOverlayClick
      >
        <Modal.Header marginBottom={{ base: 2, md: 4 }}>
          <Text fontSize="xl" fontWeight={500} marginBottom={4}>
            Edit this Media Pack
          </Text>
          <Divider width="auto" />
        </Modal.Header>

        <ModalCloseButton />

        <StepperForm
          flexProps={{ paddingX: 3, marginBottom: 6 }}
          formikConfig={{ validateOnChange: true, validateOnMount: false }}
          hideSubmit
          isSubmitAllOnStepEnabled
          onSubmit={handleSubmit}
          paddingX={3}
          steps={steps ?? []}
        >
          {({ activeStepIndex, formikState, stepsState }) => {
            const status = getStepStatus({
              activeStepIndex,
              formikState,
              mediaPack,
              stepsState,
            });

            hasChanged = status.hasChanged;

            return (
              <Modal.Footer showDivider={!isMobile} paddingY={3} paddingX={6}>
                <Box flex={1} marginX={-4}>
                  <Flex justifyContent="space-between">
                    <Button onClick={handleCancel} variant="link">
                      Cancel
                    </Button>

                    <Button
                      _disabled={{ opacity: 0.2 }}
                      colorScheme={
                        !hasChanged || !status.isValid ? 'primary' : 'secondary'
                      }
                      isDisabled={!hasChanged || !status.isValid}
                      isLoading={formikState.isSubmitting}
                      onClick={formikState.submitForm}
                      type="submit"
                    >
                      Save all changes
                    </Button>
                  </Flex>
                </Box>
              </Modal.Footer>
            );
          }}
        </StepperForm>
      </Modal>

      <ConfirmDiscardChangesOnLeaveModal
        isOpen={isDiscardModalOpen}
        onClose={onDiscardModalClose}
        onConfirm={handleDiscard}
      />
    </>
  );
};
